import React, { useContext, useEffect } from 'react';
import { useLocation } from '@reach/router';
import { RecurlyProvider } from '@recurly/react-recurly';
import { useTranslation } from 'gatsby-plugin-react-i18next';
import styled from 'styled-components';
import { Context } from '@components/Context';
import { DealModalContext } from '@components/dashboard/components/PeriodPainReport/DashboardModalContext';
import { ReportPDFProvider } from '@components/dashboard/components/PeriodPainReport/ReportPDFContext';
import { VMSAccountContainer } from '@components/dashboard/components/VMSAccountContainer';
import VMSNav from '@components/dashboard/components/VMSNav';
import { useAccountStore } from '@components/dashboard/context/UserDashboardContext';
import useCustomer from '@components/dashboard/utils/useCustomer';
import Layout from '@components/Layout';
import useDesktop from '@components/shared/useDesktop';
import { SEOProps, PageOptions } from '@contracts';
import HelpIcon from '@images/vms-account/vms-icons/Help.svg';
import MicrobiomeIcon from '@images/vms-account/vms-icons/Microbiome.svg';
import NewIcon from '@images/vms-account/vms-icons/New.svg';
import OrderIcon from '@images/vms-account/vms-icons/Order.svg';
import { pageIsFocusPage } from '@utils/helpers';
import useNavigate from '@utils/useNavigate';
import useRedirectUnauthorisedUsers from '@utils/useRedirectUnauthorisedUsers';
import useHealthProfile from '../hooks/useHealthProfile';
import useRecurlyLoaded from '../hooks/useRecurlyLoaded';
import ExpandableFAB from './ExpandableFAB';
import { PeriodPainReportProvider } from './PeriodPainReport/PeriodPainReportContext';

interface Props {
  SEO?: SEOProps;
  children: React.ReactNode;
  noUnauthorisedRedirect?: boolean;
}

const DashboardPageWrapper: React.FC<Props> = ({ SEO, children, noUnauthorisedRedirect = false }) => {
  const { navigateWrapper } = useNavigate();
  const { t } = useTranslation();
  const desktop = useDesktop(1024);
  const location = useLocation();
  const { state, dispatch } = useContext(Context);
  const { firebaseAuth, firebaseLoaded } = state;
  const userInfo = useAccountStore((accountState) => accountState.userInfo);
  const isFocusPage = pageIsFocusPage(location.pathname);
  const customer = useCustomer(state);
  const { getProfile, errorGetProfile } = useHealthProfile();

  useRedirectUnauthorisedUsers(noUnauthorisedRedirect);

  const recurlyLoaded = useRecurlyLoaded();

  useEffect(() => {
    if (!firebaseLoaded) {
      return;
    }
    if (firebaseAuth?.currentUser) {
      getCustomerData();
    }
  }, [firebaseLoaded, firebaseAuth?.currentUser]);

  useEffect(() => {
    if (firebaseAuth?.currentUser && userInfo.isMfaEnabled) {
      getResults();
    }
  }, [firebaseAuth?.currentUser, userInfo.isMfaEnabled]);

  useEffect(() => {
    if (errorGetProfile && userInfo.isMfaEnabled) {
      dispatch({ type: 'setStatusMessage', data: t('dashboard-error.health-profile-load', { message: errorGetProfile }) });
    }
  }, [errorGetProfile, userInfo.isMfaEnabled]);

  const getCustomerData = async () => {
    await customer.getDetails();
  };

  const getResults = async () => {
    const googleIdToken = await firebaseAuth?.currentUser?.getIdToken();
    getProfile(googleIdToken ?? '');
  };

  const visitHelpCentre = () => {
    window.open('https://yourdaye.zendesk.com/hc/en-us');
  };

  const EnabledFABItems = [
    { icon: OrderIcon, text: 'Order a new test kit', onClick: () => navigateWrapper(`/dashboard/${PageOptions.OrderNewKit}/`) },
    { icon: NewIcon, text: 'Activate a test kit', onClick: () => navigateWrapper(`/dashboard/${PageOptions.ActivateTest}/`) },
    { icon: HelpIcon, text: 'Go to our help centre', onClick: () => visitHelpCentre() }
  ];

  const DefaultFABItems = [
    {
      icon: MicrobiomeIcon,
      text: 'Learn more about microbiome screening',
      onClick: () => navigateWrapper(`/dashboard/${PageOptions.MicrobiomeScreening}/${PageOptions.WhatIsMicrobiomeScreening}/`)
    },
    { icon: HelpIcon, text: 'Go to our help centre', onClick: () => visitHelpCentre() }
  ];

  if (typeof window === 'undefined') return null;

  if (!recurlyLoaded) {
    return null;
  }

  return (
    <DealModalContext.ModalProvider>
      <ReportPDFProvider>
        <PeriodPainReportProvider>
          <RecurlyProvider publicKey={process.env.RECURLY_KEY} required={['cvv']}>
            <Layout isVMS={true} SEO={SEO}>
              <VMSAccountContainer>
                {desktop && !isFocusPage && <VMSNav />}
                <PageContainer $focusPage={isFocusPage}>{children}</PageContainer>
                {!isFocusPage && <ExpandableFAB items={userInfo.isMfaEnabled ? EnabledFABItems : DefaultFABItems} />}
              </VMSAccountContainer>
            </Layout>
          </RecurlyProvider>
        </PeriodPainReportProvider>
      </ReportPDFProvider>
    </DealModalContext.ModalProvider>
  );
};

export default DashboardPageWrapper;

const PageContainer = styled.div<{ $focusPage: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  margin: 0 auto;
  max-width: 100%;
  padding-inline: 30px;
  transition: opacity 150ms cubic-bezier(0.45, 0.61, 0.83, 0.67);
  margin-left: ${(props) => !props.$focusPage && '-216px'};

  ${(props) => {
    if (props.$focusPage) {
      return `width: auto; margin: 40px auto 100px;`;
    }
    return '';
  }}

  @media (max-width: 1200px) {
    ${(props) => {
      if (!props.$focusPage) {
        return `margin-left: 0;`;
      }
      return '';
    }}
  }

  @media (max-width: 768px) {
    padding-inline: 0px;
    margin: 0 auto;
    padding-bottom: 0;
  }
`;
